import React from 'react';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LinkIcon from '@mui/icons-material/Link';
import SQLResultMessage from './SQLResultMessage';
import SuggestionsMessage from './SuggestionsMessage';
import PreChatMessage from './PreChatMessage';
import OnlySQLStatementTab from '../OnlySQLStatementTab';
import UserMessage from './UserMessage';
import ChatFeedback from './ChatFeedback';
import { formatSQL } from '../../utils/sqlFormatter';
import { MessageType, MessageComponentProps, FeedbackConfig } from '../../types/messageTypes';

// Default feedback configuration
const defaultFeedbackConfig: FeedbackConfig = {
    enabledMessageTypes: [
        MessageType.BOT,
        MessageType.COMPLETE,
        MessageType.SQL_RESULT,
        MessageType.SUGGESTIONS,
        MessageType.SQL_QUERY,
        // MessageType.PRE_CHAT,
        // MessageType.TMP_QUERY
    ]
};



// Icon mapping for different message types
const messageIcons = {
    [MessageType.USER]: PersonIcon,
    [MessageType.BOT]: SmartToyIcon,
    [MessageType.COMPLETE]: PsychologyIcon,
    [MessageType.TMP_QUERY]: LinkIcon,
};

// Message class name mapping
const messageClassNames = {
    [MessageType.USER]: 'user-message',
    [MessageType.BOT]: 'bot-message',
    [MessageType.COMPLETE]: 'complete-message',
    [MessageType.PRE_CHAT]: 'pre-chat-message',
    [MessageType.TMP_QUERY]: 'tmp-query-message',
};

export const MessageFactory: React.FC<MessageComponentProps> = ({
    message,
    executeSQLStatement,
    handleSubmitWrapper,
    setPromptId,
    setSqlQuery,
    feedbackConfig = defaultFeedbackConfig,
}) => {
    // Helper function to render ChatFeedback if enabled
    const renderChatFeedback = () => {
        // Merge config with defaults
        const config = { ...defaultFeedbackConfig, ...feedbackConfig };

        // STRICT LOGIC: Both conditions must be met
        // 1. message.showFeedback must be true
        // 2. message type must be in enabledMessageTypes

        // Check if message explicitly wants feedback
        if (message.showFeedback !== true) {
            return null;
        }

        // Check if message type is enabled in config
        const isTypeEnabled = config.enabledMessageTypes?.includes(message.type);
        if (!isTypeEnabled) {
            return null;
        }

        // Both conditions met - render feedback
        return (
            <ChatFeedback
                promptId={message.prompt_id}
                semanticModel={message.semantic_model}
                accountName={message.account_name}
                tableData={message.type === MessageType.SQL_RESULT ? {
                    columns: message.columns,
                    data: message.data,
                    columnMetadata: message.columnMetadata
                } : undefined}
            />
        );
    };
    // Check if it's a special message type that has its own component
    if (message.type === MessageType.SQL_RESULT) {
        return (
            <>
                <SQLResultMessage
                    message={message}
                    executeSQLStatement={executeSQLStatement || (() => { })}
                />
                {renderChatFeedback()}
            </>
        );
    }

    if (message.type === MessageType.SUGGESTIONS) {
        return (
            <>
                <SuggestionsMessage
                    suggestions={message.suggestions || []}
                    handleSubmitWrapper={handleSubmitWrapper}
                />
                {renderChatFeedback()}
            </>
        );
    }

    if (message.type === MessageType.SQL_QUERY) {
        return (
            <>
                <OnlySQLStatementTab
                    message={message}
                    executeSQLStatement={executeSQLStatement || (() => { })}
                />
                {renderChatFeedback()}
            </>
        );
    }

    if (message.type === MessageType.PRE_CHAT) {
        return (
            <>
                <PreChatMessage
                    message={message}
                    handleSubmitWrapper={handleSubmitWrapper}
                />
                {renderChatFeedback()}
            </>
        );
    }

    // Handle tmp_query type specifically
    if (message.type === MessageType.TMP_QUERY) {
        return (
            <>
                <div className={`chat-message ${messageClassNames[message.type]}`}>
                    <div className="icon-text-container">
                        <LinkIcon color="primary" className="message-icon" />
                        <h4>
                            <b>Received SQL Query: Executing...</b>
                        </h4>
                    </div>
                    <pre className="query-block">
                        <code
                            dangerouslySetInnerHTML={{ __html: formatSQL(message.sql || '') }}
                        />
                    </pre>
                </div>
                {renderChatFeedback()}
            </>
        );
    }

    // Handle standard message types (user, bot, complete)
    const IconComponent = messageIcons[message.type as keyof typeof messageIcons];
    const className = messageClassNames[message.type as keyof typeof messageClassNames] || '';

    return (
        <>
            <div className={`chat-message ${className}`}>
                {IconComponent && (
                    <IconComponent
                        className="message-icon"
                        color="primary"
                        style={
                            message.type === MessageType.COMPLETE
                                ? { height: '40px', width: '40px' }
                                : undefined
                        }
                    />
                )}

                {message.type === MessageType.COMPLETE && (
                    <div className="icon-text-container">
                        <h4>
                            <b>Smart Insights:</b>
                        </h4>
                    </div>
                )}

                {message.type === MessageType.USER ? (
                    <UserMessage text={message.text} promptId={message.prompt_id} />
                ) : message.type === MessageType.COMPLETE ? (
                    <p>{message.text}</p>
                ) : (
                    message.text
                )}
            </div>
            {renderChatFeedback()}
        </>
    );
};
